

import { User, Settings, LogOut, HelpCircle, ChevronDown } from 'lucide-react'
import { backendApi } from '@/lib/api/backend'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export function UserMenu() {
  const navigate = useNavigate()
  const [userName, setUserName] = useState<string>('User')

  useEffect(() => {
    const getUserData = async () => {
      try {
        const { user } = await backendApi.auth.getSession()
        if (user) {
          setUserName(user.fullName || user.email?.split('@')[0] || 'User')
        }
      } catch (error) {
        console.error('Failed to get user session:', error)
      }
    }
    getUserData()
  }, [])

  const handleSignOut = async () => {
    await backendApi.auth.signout()
    navigate('/login')
    
  }

  const userInitial = userName.charAt(0).toUpperCase()
  const tier = 'Free Tier' // TODO: Get from user metadata or organization data

  /**
   * Get avatar background color based on subscription tier
   * - Free Tier: Green (bg-green-500)
   * - Pro Tier: Teal (bg-teal-500)
   * - Enterprise Tier: Indigo (bg-indigo-600)
   */
  const getAvatarColorByTier = (tierName: string): string => {
    const lowerTier = tierName.toLowerCase()

    if (lowerTier.includes('enterprise')) {
      return 'bg-indigo-600'
    } else if (lowerTier.includes('pro')) {
      return 'bg-teal-500'
    } else {
      // Default to Free Tier (green)
      return 'bg-green-500'
    }
  }

  const avatarColor = getAvatarColorByTier(tier)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1a2e] transition-colors">
        {/* Avatar - Color based on subscription tier */}
        <div className={`flex items-center justify-center w-9 h-9 rounded-full ${avatarColor} text-white font-semibold text-sm`}>
          {userInitial}
        </div>

        {/* User Info */}
        <div className="hidden sm:flex flex-col items-start">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{userName}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">{tier}</span>
        </div>

        {/* Dropdown Arrow */}
        <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[240px]">
        {/* User Info Header */}
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{userName}</span>
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">{tier}</span>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Menu Items */}
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          <User className="h-4 w-4 mr-3 text-gray-600 dark:text-gray-400" />
          <span>My Profile</span>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => navigate('/settings')}>
          <Settings className="h-4 w-4 mr-3 text-gray-600 dark:text-gray-400" />
          <span>Settings</span>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => navigate('/support')}>
          <HelpCircle className="h-4 w-4 mr-3 text-gray-600 dark:text-gray-400" />
          <span>Help & Support</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleSignOut} className="text-red-600 dark:text-red-400">
          <LogOut className="h-4 w-4 mr-3" />
          <span>Log Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
